// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Truck, Snowflake, PenLine, RefreshCw, AlertTriangle, Plus, Package, Printer, XCircle, Search, X, Download, CheckCircle2, Circle, Send } from 'lucide-react'
import * as api from '../../api/client'
import { formatPhone } from '../../api/format'
import { Pager, preferredPageSize, rememberPageSize } from '../../components/Pager'
import { useAuth } from '../../auth/AuthContext'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import CreateShipmentDrawer from './CreateShipmentDrawer'
import ShipmentDetailDrawer from './ShipmentDetailDrawer'
import { ColumnFilterButton, ColumnFilterChips, funnelToServerOps, type ColumnFilters } from '../../components/common/ColumnFilter'
// D-SOC-6: the site-of-care filter is a NEW dropdown, so it uses SearchableSelect even though
// this file is grandfathered on the Gate 19 allowlist — the allowlist covers the existing raw
// <select>s, it is not licence to add more. The facility list is also long enough to need search.
import SearchableSelect from '../../components/common/SearchableSelect'
import { carrierLabel } from '../../lib/carriers'

/**
 * Shipments worklist (Sprint 18) — the fulfillment cockpit.
 * Lists all shipments for the pharmacy; exceptions are surfaced first (server-sorted).
 * Row click opens the shipment drawer.
 *
 * Compliance: shows patient name + Rx# + drug, NOT the delivery address
 * (address lives only in the Rx detail / ship drawer where it's needed).
 *
 * ── D-RXFILTER-4: ONE MODE ──────────────────────────────────────────────────────────────────
 * This page used to have two. `serverMode` required no status chip, no date, no type, no
 * mapping, no creator, no combined flag, NO column filter and the default sort; satisfy all of
 * that and you got a server page, and the moment you touched anything the browser fetched the
 * newest 1,000 rows and filtered them locally — with no banner saying so. On ASP (~13.9k
 * shipments) clicking "Delivered" showed the newest 1,000 while the summary tile directly above
 * showed the true count.
 *
 * Every filter and the sort are query parameters now. There is no client pipeline, no cap, and
 * "next page" is always a network call.
 *
 * The toolbar controls that duplicate a column (status chips, COD, Created by, From/To) are a
 * SECOND AFFORDANCE over the same filter state — not a second filter. That is the only way a
 * chip and a funnel cannot disagree, and it makes the status chips multi-select for free.
 */

const STATUS_FILTERS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'created', label: 'Created' },
  { key: 'label_purchased', label: 'Label purchased' },
  { key: 'in_transit', label: 'In transit' },
  { key: 'out_for_delivery', label: 'Out for delivery' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'exception', label: 'Exception' },
]

/** Display units are not all 1:1 with registry columns; this is the one place that maps them. */
const REGISTRY_KEY: Record<string, string> = {
  patient: 'patient_name',
  rx: 'rx_number',
  tracking: 'tracking_number',
  service: 'service_level',
  price: 'carrier_amount',
  copay: 'copay_amount',
  shipped: 'shipped_at',
}
const regKey = (uiKey: string) => REGISTRY_KEY[uiKey] ?? uiKey

/** Range tokens the date/number funnels write into a ColumnFilters set. */
const GTE = '≥', LTE = '≤'
const boundOf = (set: Set<string> | undefined, tok: string) =>
  set ? (Array.from(set).find(t => t.startsWith(tok))?.slice(1) ?? '') : ''
const withBounds = (from: string, to: string) => {
  const next = new Set<string>()
  if (from) next.add(GTE + from)
  if (to) next.add(LTE + to)
  return next
}

export default function ShipmentsPage() {
  const { hasPermission } = useAuth()
  const canCreate = hasPermission('shipping.create')
  // Money policy (matches BuildPodDocAsync): carrier $ amounts appear ONLY with shipping.config
  // (Owner/Manager/Admin). The server also redacts carrier_amount for everyone else.
  const canSeeCost = hasPermission('shipping.config')
  const [items, setItems] = useState<api.ShipmentSummary[]>([])
  const [refreshingActive, setRefreshingActive] = useState(false)
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null)
  const [mappingFilter, setMappingFilter] = useState<'' | 'mapped' | 'unmapped'>('')
  const [search, setSearch] = useState('')
  // Search is a SERVER predicate, and on THIS list it is the one shape with a timeout history
  // (D-PERF-3: parameter sniffing on @q). A full second is deliberate — per-keystroke round trips
  // are the load-multiplier shape that took ASP down on 2026-08-06.
  const debouncedSearch = useDebouncedValue(search, 1000)
  const [typeFilter, setTypeFilter] = useState<'' | 'rx' | 'manual' | 'imported'>('')
  // D-SOC-6: '' = no filter, 'any'/'none' = went to a facility or did not, or a facility id.
  const [socFilter, setSocFilter] = useState('')
  const [socFacilities, setSocFacilities] = useState<api.FacilityRow[]>([])
  // Genuinely lazy: set on first interaction with the control, so a session that never filters by
  // site of care never pays for the directory. (This flag used to be absent and the fetch ran on
  // every mount while the comment claimed otherwise.)
  const [socWanted, setSocWanted] = useState(false)
  // The directory is server-capped (listFacilities returns `truncated`). Ignoring it would make a
  // facility past the cap look deleted rather than unlisted, so it is surfaced instead.
  const [socTruncated, setSocTruncated] = useState(false)
  const [combinedOnly, setCombinedOnly] = useState(false)
  const [creators, setCreators] = useState<api.ShipmentCreator[]>([])
  const [courierNames, setCourierNames] = useState<string[]>([])
  const [summary, setSummary] = useState<api.ShipmentsSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  // Quick-ship deep link (?ship_rx=<rxId>) from Rx / patient / provider views.
  const [searchParams, setSearchParams] = useSearchParams()
  const shipRx = searchParams.get('ship_rx')
  const shipRxMore = searchParams.get('ship_rx_more')   // ASP-60 multi-select ids (csv)
  const fullScreen = searchParams.get('full') === '1'   // ASP-61
  useEffect(() => { if (shipRx) setCreateOpen(true) }, [shipRx])
  const clearShipRx = () => {
    if (!shipRx && !shipRxMore && !fullScreen) return
    searchParams.delete('ship_rx'); searchParams.delete('ship_rx_more'); searchParams.delete('full')
    setSearchParams(searchParams, { replace: true })
  }
  const [cancelling, setCancelling] = useState<string | null>(null)
  const [detailShipmentId, setDetailShipmentId] = useState<string | null>(null)
  // Deep link: /shipments?shipment=<id> opens the drawer (chips link here for POD).
  // Reacts to the query param itself, so navigating to a new ?shipment= from within
  // the page (e.g. 'Review order' after create) opens the drawer without a remount.
  useEffect(() => {
    const q = searchParams.get('shipment')
    if (q) setDetailShipmentId(q)
  }, [searchParams])
  const [carrierLookup, setCarrierLookup] = useState<api.CarrierLookupResult | null>(null)
  const [lookingUp, setLookingUp] = useState(false)

  // ── Table controls: sort / column visibility / CSV (Patrick 2026-07-15) ──
  type SortKey = 'patient' | 'rx' | 'carrier' | 'tracking' | 'handling' | 'service' | 'price' | 'copay' | 'cod' | 'status' | 'shipped' | 'created_by'
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [srvPage, setSrvPage] = useState(1)
  const [srvPageSize, setSrvPageSize] = useState(() => preferredPageSize())
  const [srvTruncated, setSrvTruncated] = useState(false)
  // Exact server-side totals. null = the server could not count (query failed) — the pager then
  // degrades to next/prev instead of displaying a fabricated number.
  const [srvTotal, setSrvTotal] = useState<number | null>(null)
  const [srvTotalPages, setSrvTotalPages] = useState<number | null>(null)

  // Header filters. This is the SINGLE filter state: the status chips, the COD select, the
  // Created-by select and the From/To inputs all read and write it, so no two controls can hold
  // different answers to the same question.
  const [colFilters, setColFilters] = useState<ColumnFilters>({})
  const setCol = (uiKey: string, next: Set<string>) => {
    setColFilters(f => ({ ...f, [uiKey]: next }))
    setSrvPage(1)
  }

  // Capability comes from the server registry — the frontend keeps NO second list of which
  // columns are filterable. That second list is exactly how sort and filter drifted apart on the
  // Rx worklist (a 9-column backend whitelist against ~40 header funnels).
  const [caps, setCaps] = useState<Map<string, api.RxColumnCap>>(new Map())
  const [capsLoaded, setCapsLoaded] = useState(false)
  useEffect(() => {
    api.getShipmentColumnCaps()
      .then(r => setCaps(new Map(r.items.map(c => [c.key, c]))))
      .catch(() => { /* degrade to funnels disabled; never block the list */ })
      .finally(() => setCapsLoaded(true))
  }, [])

  // Translate the header filter state into registry filters. A column the server will not accept
  // is REPORTED (blockedCols), never silently dropped while the list still renders "results".
  const { shipFilters, blockedCols } = useMemo(() => {
    const pushed: api.RxFilter[] = []
    const stuck: string[] = []
    for (const [uiKey, set] of Object.entries(colFilters)) {
      if (!set || set.size === 0) continue
      const key = regKey(uiKey)
      const cap = caps.get(key)
      if (!cap?.filterable) { stuck.push(uiKey); continue }
      // One translation for every server-paged list (D-UI-2): value lists, from/to bounds and
      // the contains box all go through it, against the operators the registry admits.
      const t = funnelToServerOps(set, cap.ops)
      for (const p of t.preds) pushed.push({ key, ...p })
      if (t.blocked) stuck.push(uiKey)
    }
    // Before the capability report lands every column looks unfilterable. Reporting that would
    // flash a warning about filters that are in fact fine a moment later.
    return { shipFilters: pushed, blockedCols: capsLoaded ? stuck : [] }
  }, [colFilters, caps, capsLoaded])

  const ALL_COLUMNS: { key: SortKey; label: string }[] = [
    { key: 'patient', label: 'Patient' }, { key: 'rx', label: 'Rx' },
    { key: 'carrier', label: 'Carrier' }, { key: 'tracking', label: 'Tracking #' },
    { key: 'handling', label: 'Handling' }, { key: 'service', label: 'Service' },
    { key: 'price', label: 'Price' }, { key: 'copay', label: 'Copay' },
    // D-SHIP-COD: Copay is what the patient OWES; COD is what the courier was asked to collect
    // and whether it reports collecting it. Deliberately adjacent — the gap between the two
    // columns is the thing worth noticing, and it is invisible if they sit apart.
    { key: 'cod', label: 'COD' },
    { key: 'status', label: 'Status' },
    { key: 'shipped', label: 'Shipped' },
    { key: 'created_by', label: 'Created by' },
  ]
  // Price (carrier cost) is a manager/admin column — hidden entirely without shipping.config.
  const COLUMNS = canSeeCost ? ALL_COLUMNS : ALL_COLUMNS.filter(c => c.key !== 'price')
  const COLS_LS_KEY = 'shipments-cols'  // localStorage pref key
  const [visibleCols, setVisibleCols] = useState<Record<string, boolean>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLS_LS_KEY) || 'null')
      if (saved && typeof saved === 'object') return saved
    } catch { /* corrupted prefs -> defaults */ }
    return Object.fromEntries(ALL_COLUMNS.map(c => [c.key, true]))
  })
  const [colsOpen, setColsOpen] = useState(false)
  const toggleCol = (k: string) => setVisibleCols(v => {
    const next = { ...v, [k]: !v[k] }
    try { localStorage.setItem(COLS_LS_KEY, JSON.stringify(next)) } catch { /* storage full/blocked -> session-only */ }
    return next
  })
  const isVis = (k: SortKey) => visibleCols[k] !== false
  // Sorting is a SERVER predicate. A header whose registry column is not sortable does nothing
  // rather than quietly sorting the page you happen to be on.
  const sortable = (k: SortKey) => caps.get(regKey(k))?.sortable ?? false
  const clickSort = (k: SortKey) => {
    if (!sortable(k)) return
    if (sortKey === k) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(k); setSortDir(k === 'shipped' ? 'desc' : 'asc') }
    setSrvPage(1)
  }
  const srvSortKey = sortKey && sortable(sortKey) ? regKey(sortKey) : null

  // Load creators once for the dropdown.
  useEffect(() => {
    api.shipmentCreators().then(r => { setCreators(r.items); setCourierNames(r.courier_names || []) }).catch(() => { /* dropdown just stays minimal */ })
  }, [])

  const doCancel = async (shipmentId: string) => {
    if (!window.confirm('Cancel this shipment? If the courier has already picked it up, cancellation may be refused.')) return
    setCancelling(shipmentId)
    setError(null)
    try {
      const res = await api.cancelShipment(shipmentId)
      if (res.status !== 'cancelled') setError(res.message || 'Shipment could not be cancelled.')
      load()
    } catch (e: any) {
      setError(e?.payload?.message || e?.message || 'Cancel failed.')
    } finally {
      setCancelling(null)
    }
  }

  // The bespoke facets — the ones that genuinely are not columns of this table. Column filters
  // travel separately, as registry predicates.
  const facets = () => ({
    mapping: mappingFilter || undefined,
    q: debouncedSearch.trim() || undefined,
    type: typeFilter || undefined,
    combined: combinedOnly || undefined,
    siteOfCare: socFilter || undefined,   // D-SOC-6
  })

  // D-SOC-6: the facility directory for the site-of-care filter. Active-only, fetched once and
  // only after the operator reaches for the control — it is reference data, not a search, and a
  // round trip on a 0.5-1 vCore tenant is not worth spending on a filter nobody opened.
  useEffect(() => {
    if (!socWanted || socFacilities.length) return
    let live = true
    // A PICKER, not a list: it wants every active facility, so it asks for one big page and
    // reports truncation from the server's own total rather than a `truncated` flag the endpoint
    // no longer sends (it is paged now — see listFacilities).
    api.listFacilities(undefined, true, { page: 1, pageSize: 200 })
      .then(r => { if (live) { setSocFacilities(r.items); setSocTruncated((r.total ?? r.items.length) > r.items.length) } })
      .catch(() => { if (live) setSocFacilities([]) })
    return () => { live = false }
  }, [socWanted, socFacilities.length])

  // A stable signature of everything the server query depends on, so the effect below refetches
  // when — and only when — the QUESTION changes.
  const filterSig = api.encodeRxFilters(shipFilters).join('|')

  // quiet=true is the background refresh: no spinner, and a transient failure leaves
  // the rows on screen rather than replacing the table with an error.
  const loadingRef = useRef(false)
  const capsLoadedRef = useRef(false)
  capsLoadedRef.current = capsLoaded
  const load = useCallback(async (quiet = false) => {
    if (quiet && loadingRef.current) return
    // Hold ONLY when something actually depends on capabilities. A cold default view must not
    // wait on the caps request before showing its first page — that would put a second round
    // trip in front of every page load for a list nobody has filtered yet.
    if (!capsLoadedRef.current && (Object.keys(colFilters).length > 0 || sortKey)) return
    loadingRef.current = true
    if (!quiet) { setLoading(true); setError(null) }
    try {
      const params = facets()
      // The background refresh fetches ROWS ONLY. shipmentsSummary is an aggregate over
      // the same filter set, and the totals it produces do not change on their own — they
      // move when the filters do, which already triggers a full (non-quiet) load. Running
      // it on every tick would double the poll's cost on a 5-DTU shared database for a
      // number nobody is watching change.
      const [list, summ] = await Promise.all([
        api.listShipmentsPaged({
          ...params, page: srvPage, pageSize: srvPageSize,
          filters: shipFilters, sortKey: srvSortKey, sortDir,
        }),
        // The tiles now run the SAME predicate as the table, column filters included — they used
        // to ignore the search box entirely because the server built their WHERE separately.
        quiet ? Promise.resolve(undefined)
              : api.shipmentsSummary({ ...params, filters: shipFilters }).catch(() => null),
      ])
      setItems(list.items)
      setSrvTruncated(!!list.truncated)
      // EXACT TOTAL. Null means the count query failed server-side — keep next/prev rather than
      // show a number we know to be wrong.
      setSrvTotal(typeof list.total === 'number' ? list.total : null)
      setSrvTotalPages(typeof list.total_pages === 'number' ? list.total_pages : null)
      if (!quiet) setSummary(summ ?? null)
    } catch (e: any) {
      if (!quiet) setError(e?.body?.message || e?.message || 'Could not load shipments.')
    } finally {
      loadingRef.current = false
      if (!quiet) setLoading(false)
    }
    // EVERY input to the request MUST be listed here. load is memoised on this array and the load
    // effect below depends on `load`, so an input missing from it changes state, re-renders, and
    // never refetches — the control then looks completely dead. That is exactly how codFilter
    // shipped: read inside load, absent from the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, typeFilter, mappingFilter, combinedOnly, socFilter, filterSig, srvSortKey, sortDir, srvPage, srvPageSize, capsLoaded])

  // Any change to the QUESTION returns to page 1 — page 7 of the old result set is meaningless
  // against a new one, and asking for it just renders an empty table.
  useEffect(() => { setSrvPage(1) }, [debouncedSearch, typeFilter, mappingFilter, combinedOnly, socFilter, filterSig])

  // Single load effect. The search term is debounced upstream (see debouncedSearch), so this
  // fires once the question has settled rather than once per keystroke.
  useEffect(() => {
    setCarrierLookup(null)
    void load()
  }, [load])

  // Near-real-time list. There was no refresh of any kind here: a status that had
  // already moved server-side (pending_confirmation → label_purchased on the 1-minute
  // sweep, or a courier status flip) stayed stale on screen until a manual page
  // reload, which is what made orders look un-batchable long after they were ready.
  // Quiet refresh only, paused while the tab is hidden, plus an immediate catch-up
  // when the operator comes back to the tab.
  //
  // NOT while a search is active. Free-text search is the one shape of this query with
  // a timeout history (the covering index in mig 279 exists because of it), and the
  // non-sargable name predicate is still reachable. Re-running whatever someone typed
  // every 25s for as long as the tab is open is how a dispatch station quietly holds a
  // 5-DTU shared database down. Polling exists so a STATUS moves on its own; a search
  // result set does not go stale that way, and any filter change reloads in full anyway.
  const LIST_POLL_MS = 25000
  const pollable = search.trim().length === 0
  useEffect(() => {
    if (!pollable) return
    const h = window.setInterval(() => { if (!document.hidden) void load(true) }, LIST_POLL_MS)
    const onVis = () => { if (!document.hidden) void load(true) }
    document.addEventListener('visibilitychange', onVis)
    return () => { window.clearInterval(h); document.removeEventListener('visibilitychange', onVis) }
  }, [load, pollable])

  // The fetched rows ARE the page — there is no client pipeline left to run them through.
  //
  // TOTALS. This used to advertise `srvPage + 1` pages whenever the N+1 probe said "more exist",
  // and a total of "rows seen so far, +1" — both invented, so the pager said things like
  // "51 records, page 1 of 2" on a 14,000-row list. The endpoint returns an exact COUNT for the
  // same predicate; when it cannot (a count failure) we fall back to the probe rather than
  // render a number we know to be wrong.
  const pageRows = items
  const page = srvPage
  const setPage = (n: number) => setSrvPage(n)
  const pageSize = srvPageSize
  const setPageSize = (n: number) => { rememberPageSize(n); setSrvPageSize(n); setSrvPage(1) }
  const totalPages = srvTotalPages ?? (srvTruncated ? srvPage + 1 : srvPage)
  const total = srvTotal ?? ((srvPage - 1) * srvPageSize + items.length + (srvTruncated ? 1 : 0))

  // Second affordances over the single filter state. Each of these controls used to be its own
  // server parameter sitting beside a column funnel that answered the same question differently.
  const statusSel = colFilters['status'] ?? new Set<string>()
  const codSel = colFilters['cod'] ?? new Set<string>()
  const createdBySel = colFilters['created_by'] ?? new Set<string>()
  const fromDate = boundOf(colFilters['shipped'], GTE)
  const toDate = boundOf(colFilters['shipped'], LTE)
  const anyToolbarFilter = fromDate || toDate || typeFilter || mappingFilter || combinedOnly || createdBySel.size > 0

  // The creators endpoint IS the domain vocabulary for the Created by column — accounts by id,
  // courier-side names as themselves. It is not faceted server-side precisely because display
  // names live in the account directory, not the tenant database.
  const createdByOptions = useMemo(
    () => [...creators.map(c => c.id), ...courierNames],
    [creators, courierNames])
  const createdByLabel = useMemo(() => {
    const m = new Map(creators.map(c => [c.id, c.display_name]))
    return (v: string) => m.get(v) ?? v
  }, [creators])

  return (
    <div style={{ padding: '20px 24px', maxWidth: 'var(--page-max, 1100px)', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Truck size={20} />
        <h1 style={{ margin: 0, fontSize: 20 }}>Shipments</h1>
        <div style={{ position: 'relative' }}>
          <button type="button" onClick={() => setColsOpen(o => !o)} style={refreshBtn} title="Show/hide columns">
            Columns
          </button>
          {colsOpen && (
            <div style={colsPopover} onMouseLeave={() => setColsOpen(false)}>
              {COLUMNS.map(c => (
                <label key={c.key} style={colsItem}>
                  <input type="checkbox" checked={isVis(c.key)} onChange={() => toggleCol(c.key)} />
                  {c.label}
                </label>
              ))}
            </div>
          )}
        </div>
        {/* The page had TWO CSV buttons — this one and the link in the filter row below — and
            they had already diverged: this one appended `combined` while the other did not, and
            its own tooltip admitted column filters were missing from the export. One control,
            one predicate. The remaining link carries every filter, funnels included. */}
        <button type="button" onClick={() => load()} style={refreshBtn} title="Reload">
          <RefreshCw size={13} /> Reload
        </button>
        {/* D-SHIP-8: bulk on-demand — refresh every non-delivered shipment's status
            from the courier NOW (bounded + debounced server-side). */}
        <button type="button" style={refreshBtn} disabled={refreshingActive}
          title="Check the courier for the latest status on all non-delivered shipments now"
          onClick={async () => {
            setRefreshingActive(true); setRefreshMsg(null)
            try {
              const r = await api.refreshActiveShipments()
              setRefreshMsg(`Checked ${r.checked_count} · ${r.advanced} updated · ${r.delivered} delivered${r.failed ? ` · ${r.failed} failed` : ''}${r.capped ? ' (more remain — run again)' : ''}`)
              await load()
            } catch (e: any) {
              setRefreshMsg(e?.status === 429 ? 'A refresh is already running — please wait.' : (e?.payload?.message || e?.message || 'Refresh failed.'))
            } finally {
              setRefreshingActive(false)
            }
          }}>
          <RefreshCw size={13} /> {refreshingActive ? 'Refreshing…' : 'Refresh status'}
        </button>
        {canCreate && (
          <button type="button" onClick={() => setCreateOpen(true)} style={createBtn}>
            <Plus size={14} /> Create shipment
          </button>
        )}
      </div>
      <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 4 }}>
        Track and manage prescription deliveries across all carriers. Status updates automatically every few minutes.
      </p>
      {refreshMsg && (
        <p style={{ color: 'var(--color-primary, #2563eb)', fontSize: 13, marginTop: 2 }}>{refreshMsg}</p>
      )}


      {/* search box */}
      <div style={searchWrap}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: 9, color: 'var(--color-text-muted)' }} />
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by order #, tracking #, Rx #, patient name, address, city, ZIP…"
          style={searchInput}
        />
        {search && (
          <button type="button" onClick={() => setSearch('')} style={searchClear} title="Clear">
            <X size={13} />
          </button>
        )}
      </div>

      {/* Filter row. From/To, COD and Created by are SECOND AFFORDANCES over the header filter
          state — the same values their column funnels write — so the toolbar and the funnel can
          never hold different answers. Type, Mapping and Combined are their own controls because
          they genuinely are not columns of this table. */}
      <div style={filterRow}>
        <label style={filterLabel}>
          From
          <input type="date" value={fromDate}
                 onChange={e => setCol('shipped', withBounds(e.target.value, toDate))} style={dateInput} />
        </label>
        <label style={filterLabel}>
          To
          <input type="date" value={toDate}
                 onChange={e => setCol('shipped', withBounds(fromDate, e.target.value))} style={dateInput} />
        </label>
        <label style={filterLabel}>
          Type
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} style={selectInput}>
            <option value="">All types</option>
            <option value="rx">Rx-linked</option>
            <option value="manual">Manual</option>
            <option value="imported">Imported</option>
          </select>
        </label>
        {/* D-SHIP-COD: applied by the QUERY, so the row count, the pager, the tiles and the CSV
            all reflect it — "Not collected" means every uncollected COD in the tenant, not the
            ones that happened to be on screen. */}
        <label style={filterLabel}>
          COD
          <select value={codSel.size === 1 ? Array.from(codSel)[0] : ''}
                  onChange={e => setCol('cod', e.target.value ? new Set([e.target.value]) : new Set())}
                  style={selectInput}
                  title="Filter by whether the courier has reported collecting the COD">
            <option value="">All COD</option>
            <option value="Not collected">Not collected</option>
            <option value="Collected">Collected</option>
            <option value="No COD">No COD</option>
          </select>
        </label>
        <label style={filterLabel}>
          Mapping
          <select value={mappingFilter} onChange={e => setMappingFilter(e.target.value as any)} style={selectInput}
                  title="Mapped = resolves to a prescription (linked or matched by reference)">
            <option value="">All</option>
            <option value="mapped">Mapped to Rx</option>
            <option value="unmapped">Not mapped</option>
          </select>
        </label>
        {/* D-SOC-6: filter by where the package actually went. The directory loads on first
            interaction (see the effect above), not on mount. */}
        <label style={filterLabel}>
          Site of care
          <div style={{ width: 210 }} onPointerDownCapture={() => setSocWanted(true)} onFocusCapture={() => setSocWanted(true)}>
            <SearchableSelect
              value={socFilter}
              ariaLabel="Filter by site of care"
              placeholder="All"
              emptyText={socWanted && !socFacilities.length ? 'Loading facilities…' : 'No facilities in the directory.'}
              options={[
                { value: '', label: 'All' },
                { value: 'any', label: 'Any facility' },
                { value: 'none', label: 'Not a facility delivery' },
                ...socFacilities.map(f => ({
                  value: f.id,
                  label: f.name,
                  hint: [f.city, f.state_code].filter(Boolean).join(', '),
                })),
              ]}
              onChange={setSocFilter}
            />
            {/* An unlisted facility would otherwise look deleted rather than past the cap. */}
            {socTruncated && (
              <div style={{ fontSize: 10.5, color: 'var(--color-text-muted, #6b7280)', marginTop: 2 }}>
                Showing the first {socFacilities.length}. Search by name in the box above.
              </div>
            )}
          </div>
        </label>
        <label style={filterLabel}>
          Combined
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', height: 32 }}
                 title="Show only orders that had an Rx added after the label was purchased (pharmacist-error combines)">
            <input type="checkbox" checked={combinedOnly} onChange={e => setCombinedOnly(e.target.checked)} style={{ width: 15, height: 15, cursor: 'pointer' }} />
            <span>Combined only</span>
          </label>
        </label>
        {(creators.length > 1 || courierNames.length > 0) && (
          <label style={filterLabel}>
            Created by
            <select value={createdBySel.size === 1 ? Array.from(createdBySel)[0] : ''}
                    onChange={e => setCol('created_by', e.target.value ? new Set([e.target.value]) : new Set())}
                    style={selectInput}
                    title={createdBySel.size > 1 ? 'Several creators selected — use the column funnel to see them all' : undefined}>
              <option value="">{createdBySel.size > 1 ? `${createdBySel.size} selected` : 'Anyone'}</option>
              {creators.map(c => <option key={c.id} value={c.id}>{c.display_name}</option>)}
              {courierNames.length > 0 && (
                <optgroup label="Placed with courier">
                  {courierNames.map(n => <option key={n} value={n}>{n}</option>)}
                </optgroup>
              )}
            </select>
          </label>
        )}
        {anyToolbarFilter && (
          <button type="button" style={clearFiltersBtn}
            onClick={() => {
              setTypeFilter(''); setMappingFilter(''); setCombinedOnly(false)
              setColFilters(f => ({ ...f, shipped: new Set(), created_by: new Set() }))
              setSrvPage(1)
            }}>
            Clear filters
          </button>
        )}
        <span style={{ flex: 1 }} />
        {/* The export runs the identical predicate as the screen, column filters INCLUDED. They
            used to be page-scoped display filters the export could not see, so a CSV pulled under
            an active funnel silently contained more rows than the table showed. */}
        <a href={api.shipmentsExportCsvUrl({ ...facets(), filters: shipFilters })}
           download="shipments.csv" style={csvBtn} title="Download every row matching the current filters">
          <Download size={13} /> CSV
        </a>
      </div>

      {/* Summary panel — appears when a date range is set OR results are non-trivial */}
      {summary && summary.total_count > 0 && (fromDate || toDate || summary.total_count >= 5) && (
        <div style={summaryPanel}>
          <SummaryStat label="Shipments" value={String(summary.total_count)} />
          {canSeeCost && summary.total_carrier_amount != null && (
            <SummaryStat label="Cost (carrier)" value={`$${summary.total_carrier_amount.toFixed(2)}`} />
          )}
          <SummaryStat label="Delivered" value={String(summary.delivered_count)} accent="#15803d" />
          <SummaryStat label="Active" value={String(summary.active_count)} accent="#0369a1" />
          {summary.exception_count > 0 && (
            <SummaryStat label="Exceptions" value={String(summary.exception_count)} accent="#dc2626" />
          )}
          <SummaryStat label="Rx / Manual / Import"
            value={`${summary.rx_count} / ${summary.manual_count} / ${summary.imported_count}`} />
          {summary.avg_delivery_minutes != null && (
            <SummaryStat label="Avg delivery"
              value={formatMinutes(summary.avg_delivery_minutes)} />
          )}
        </div>
      )}

      {/* Status chips — a second affordance over the Status column filter, not a second filter.
          Because they write the same multi-value set the funnel does, they are multi-select now:
          ticking Exception and On hold together is one query, and the chip row and the funnel
          always agree because there is only one answer to agree about. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '14px 0' }}>
        {STATUS_FILTERS.map(f => {
          const on = f.key ? statusSel.has(f.key) : statusSel.size === 0
          return (
            <button
              key={f.key || 'all'}
              type="button"
              onClick={() => {
                if (!f.key) return setCol('status', new Set())
                const next = new Set(statusSel)
                if (next.has(f.key)) next.delete(f.key); else next.add(f.key)
                setCol('status', next)
              }}
              style={{ ...chip, ...(on ? chipActive : {}) }}
            >
              {f.label}
            </button>
          )
        })}
      </div>

      {/* A filter the server will not accept is STATED, never silently dropped while the table
          still renders "results". This is the D-SCALE-1 flavour-C rule the whole change exists
          to enforce. */}
      {blockedCols.length > 0 && (
        <div style={{ fontSize: 12.5, color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 10px', marginBottom: 10 }}>
          Not filtered on the server: {blockedCols.join(', ')}. These results ignore those
          selections — clear them to see an accurate list.
        </div>
      )}

      {error && <div style={errorBox}>{error}</div>}

      {loading ? (
        <div style={mutedText}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={emptyBox}>
          <Truck size={28} style={{ color: 'var(--color-text-muted)', marginBottom: 8 }} />
          <div style={{ fontWeight: 600 }}>
            {search
              ? `No local match for "${search}"`
              : statusSel.size > 0
                ? `No shipments with status ${Array.from(statusSel).map(statusLabel).join(', ')}`
                : 'No shipments yet'}
          </div>
          <div style={mutedText}>
            {search
              ? 'Nothing in OwlRx matches that Rx, name, address, phone, or order #. The courier can only be searched by its ORDER number — if you have one, look it up directly:'
              : 'Ship a dispensed prescription from its detail page to see it here.'}
          </div>
          {search && canCreate && (
            <div style={{ marginTop: 12 }}>
              <button type="button" disabled={lookingUp} style={primaryBtnSmall}
                onClick={async () => {
                  setLookingUp(true); setCarrierLookup(null)
                  try { setCarrierLookup(await api.carrierOrderLookup(search.trim())) }
                  catch (e: any) { setError(e?.message || 'Lookup failed.') }
                  finally { setLookingUp(false) }
                }}>
                {lookingUp ? 'Looking up…' : `Look up "${search.trim()}" on the courier`}
              </button>
            </div>
          )}
          {carrierLookup && (
            <div style={{ marginTop: 14, textAlign: 'left', maxWidth: 620, marginLeft: 'auto', marginRight: 'auto' }}>
              {carrierLookup.found
                ? <CarrierLookupCard
                    r={carrierLookup}
                    onSaved={() => { setCarrierLookup(null); setSearch(''); load() }}
                    onError={(msg) => setError(msg)}
                  />
                : (
                  <div style={lookupBox}>
                    <div style={{ fontWeight: 600 }}>Not found on the courier</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>{carrierLookup.detail || 'The courier could not locate this order.'}</div>
                  </div>
                )}
            </div>
          )}
        </div>
      ) : (
        <>
        <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="shipments" />
        <div style={tableWrap}>
          <ColumnFilterChips filters={colFilters}
            labels={Object.fromEntries(COLUMNS.map(c => [c.key, c.label]))}
            labelFor={(col, v) => (col === 'created_by' ? createdByLabel(v) : v)}
            onClear={k => setCol(k, new Set())}
            onClearAll={() => { setColFilters({}); setSrvPage(1) }} />
          <table style={table}>
            <thead>
              <tr>
                {COLUMNS.filter(c => isVis(c.key)).map(c => {
                  // Capability is the SERVER's answer, per column: what may be sorted, what may
                  // be filtered, and how options are discovered. The frontend holds no second
                  // list — the drift between a 9-column sort whitelist and ~40 header funnels is
                  // exactly what that second list produced on the Rx worklist.
                  const cap = caps.get(regKey(c.key))
                  const canSort = cap?.sortable ?? false
                  const isDate = cap?.klass === 'date'
                  const isNumeric = cap?.klass === 'numeric'
                  // Created by is the one column whose options are NOT a facet: they come from
                  // /shipments/creators, which is the domain vocabulary for it (display names live
                  // in the account directory, not the tenant database). The list is complete and
                  // small, so the funnel filters it in memory — zero requests per keystroke.
                  const localOptions = c.key === 'created_by' ? createdByOptions : undefined
                  // D-UI-2: a column the registry filters by CONTAINS and never lists (a patient
                  // name, an Rx number, a tracking number) gets the funnel with a text box in it.
                  const isText = !!cap?.filterable && !cap.facetable && !isDate && !isNumeric && !localOptions && cap.ops.includes('contains')
                  const hasFunnel = !!cap?.filterable && (cap.facetable || isDate || isNumeric || !!localOptions || isText)
                  return (
                  <th key={c.key} style={{ ...th, cursor: canSort ? 'pointer' : 'default', userSelect: 'none' }}
                      onClick={() => clickSort(c.key)}
                      title={canSort ? `Sort by ${c.label}` : (cap?.reason ?? `${c.label} cannot be sorted`)}>
                    {c.label}{sortKey === c.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}{' '}
                    {hasFunnel ? (
                      <ColumnFilterButton label={c.label}
                        isDate={isDate}
                        isNumeric={isNumeric}
                        isText={isText}
                        values={localOptions}
                        labelFor={c.key === 'created_by' ? createdByLabel : undefined}
                        fetchOptions={(localOptions || isDate || isNumeric || isText) ? undefined : (term: string) => api.getShipmentFacet({
                          column: regKey(c.key),
                          q: term || undefined,
                          // Every OTHER active filter travels with the request, so an option that
                          // would return nothing given what is already ticked is not offered. The
                          // column's own filter is excluded server-side, or ticking one value
                          // would hide the rest and there would be no way to widen the selection.
                          filters: shipFilters,
                        })}
                        selected={colFilters[c.key] ?? new Set()}
                        onChange={next => setCol(c.key, next)} />
                    ) : (
                      // A funnel that cannot list values says SO, and says why. Patient names, Rx
                      // numbers and tracking numbers are refused by the registry on purpose:
                      // searchable, never enumerable.
                      <span title={cap?.reason ?? `${c.label} cannot be filtered on the server.`}
                        aria-label={`${c.label} filter unavailable`}
                        style={{ display: 'inline-block', width: 14, opacity: 0.25, cursor: 'not-allowed', fontSize: 11 }}>⌀</span>
                    )}
                  </th>
                )})}
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(s => {
                // BUG-002 fix: for imports the carrier order number IS the tracking
                // number — same value, no separate FedEx-style tracking code. Show it
                // in the Rx column labeled 'Order #', and blank the Tracking column
                // when they'd be identical (avoids the visual duplication that
                // looked like a bug — same number repeated in two columns).
                const isImport = !!s.imported_from_courier_at
                // Imports now carry the courier piece Reference (portal Ref#) = the real
                // Rx number; prefer it over the order number in the Rx column.
                const importRx = isImport ? (s.carrier_reference || null) : null
                const rxOrOrderLabel = (s.rx_number || importRx) ? 'Rx' : (isImport ? 'Order #' : '')
                // Non-imported rows never show the courier order number under Rx —
                // that's tracking, not a prescription (Patrick 2026-07-16).
                const rxOrOrderValue = s.rx_number || importRx || (isImport ? s.carrier_shipment_id : null) || '—'
                // When the Rx column shows the real Rx (Ref#), surface the order number
                // in the Tracking column instead of blanking it — otherwise it appears
                // nowhere in the table. Pre-Ref# imports keep the old dedup blanking.
                const trackingDisplay = (isImport && s.tracking_number === s.carrier_shipment_id)
                  ? (importRx ? `Order ${s.carrier_shipment_id}` : '—')
                  : (s.tracking_number || '—')
                return (
                <tr key={s.id} style={{ ...row, cursor: 'pointer' }}
                    onClick={() => setDetailShipmentId(s.id)}
                    title="View shipment details">
                  {isVis('patient') && (
                  <td style={td}>
                    {s.patient_name || '—'}
                    {s.is_manual && <span style={manualTag}><Package size={9} /> Manual</span>}
                    {isImport && <span style={importedTag}>Imported</span>}
                  </td>
                  )}
                  {isVis('rx') && (
                  <td style={td}>
                    <div>
                      {(() => {
                        const refs = (s as any).rx_refs as { rx_number: string; rx_id: string }[] | undefined
                        const linkStyle = { color: 'var(--color-primary, #2563eb)', textDecoration: 'none', fontWeight: 600 } as const
                        // Multi-Rx order: link EACH Rx number to its own prescription.
                        if (refs && refs.length > 0) {
                          return refs.map((ref, i) => (
                            <span key={ref.rx_id}>
                              {i > 0 && <span style={{ color: 'var(--color-text-muted)' }}>, </span>}
                              <a href={`/rx/${ref.rx_id}?from=shipment&sid=${s.id}`}
                                 onClick={e => { e.stopPropagation(); }}
                                 style={linkStyle} title={`Open Rx ${ref.rx_number}`}>{ref.rx_number}</a>
                            </span>
                          ))
                        }
                        // FK-linked but no token parse (manual/created rows): single link.
                        if (s.rx_item_id) return (
                          <a href={`/rx/${s.rx_fill_item_id || s.rx_item_id}?from=shipment&sid=${s.id}`}
                             onClick={e => { e.stopPropagation(); }}
                             style={linkStyle} title="Open this prescription">{rxOrOrderValue}</a>
                        )
                        // No resolvable Rx (order # only): plain text.
                        return rxOrOrderValue
                      })()}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                      {s.copay_amount != null && s.copay_amount > 0
                        ? <b style={{ color: '#b45309' }}>COLLECT ${s.copay_amount.toFixed(2)}{s.drug_name ? ` · ${s.drug_name}` : ''}</b>
                        : (s.drug_name || rxOrOrderLabel)}
                    </div>
                  </td>
                  )}
                  {isVis('carrier') && (
                  <td style={td}>{carrierLabel(s.carrier)}</td>
                  )}
                  {isVis('tracking') && (
                  <td style={td}>{trackingDisplay}</td>
                  )}
                  {isVis('handling') && (
                  <td style={td}>
                    <span style={{ display: 'flex', gap: 4 }}>
                      {s.cold_chain && <Snowflake size={13} style={{ color: '#0369a1' }} aria-label="Cold chain" />}
                      {s.require_signature && <PenLine size={13} style={{ color: '#7c3aed' }} aria-label="Signature" />}
                    </span>
                  </td>
                  )}
                  {isVis('service') && (
                  <td style={td}>{s.service_level ? <span style={{ fontSize: 11.5, fontWeight: 600, padding: '2px 7px', borderRadius: 6, background: 'var(--color-surface-2, #f1f5f9)' }}>{s.service_level}</span> : '—'}</td>
                  )}
                  {canSeeCost && isVis('price') && (
                  <td style={td}>{s.carrier_amount != null ? `$${s.carrier_amount.toFixed(2)}` : '—'}</td>
                  )}
                  {isVis('copay') && (
                  <td style={td}>{s.copay_amount != null && s.copay_amount > 0
                    ? <b style={{ color: '#b45309' }}>${s.copay_amount.toFixed(2)}</b> : '—'}</td>
                  )}
                  {isVis('cod') && (
                  <td style={td}>{(() => {
                    // No COD on this order in EITHER direction — nothing sent and the carrier has
                    // reported nothing. Em dash so it reads apart from a real zero.
                    if (s.cod_amount_sent == null && s.cod_status == null) return '—'
                    // CARRIER-SIDE COD WE DID NOT SEND (F3). Imported orders — the bulk of ASP's
                    // shipments — are created directly in Roadpas, so they never carry
                    // cod_amount_sent. Keying the whole cell off that field rendered an em dash on
                    // orders where the driver HAD collected money, hiding exactly the population
                    // this column exists to expose. Fall back to what the carrier reports.
                    if (s.cod_amount_sent == null) {
                      const got = s.cod_amount_received
                      return s.cod_collected ? (
                        <span title={`Collected by the courier${s.cod_collected_at ? ` ${new Date(s.cod_collected_at).toLocaleString()}` : ''} — not sent by OwlRx (imported order)`}>
                          <b style={{ color: '#15803d' }}>{got != null ? `$${got.toFixed(2)}` : 'Collected'}</b>
                          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#15803d', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 10, padding: '1px 7px' }}>
                            COLLECTED
                          </span>
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }} title={`Carrier CodStatus: ${s.cod_status}`}>
                          {got != null ? `$${got.toFixed(2)}` : 'carrier COD'}
                        </span>
                      )
                    }
                    const amt = `$${s.cod_amount_sent.toFixed(2)}`
                    if (s.cod_collected) {
                      // Prefer the carrier's OWN reported figure when it differs from what we
                      // asked for — a mismatch is the single most useful thing this column can
                      // show, and hiding it behind our own number would defeat the point.
                      const got = s.cod_amount_received
                      const short = got != null && Math.abs(got - s.cod_amount_sent) >= 0.01
                      return (
                        <span title={s.cod_collected_at ? `Collected ${new Date(s.cod_collected_at).toLocaleString()}` : 'Collected'}>
                          <b style={{ color: '#15803d' }}>{got != null ? `$${got.toFixed(2)}` : amt}</b>
                          <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#15803d', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 10, padding: '1px 7px' }}>
                            COLLECTED
                          </span>
                          {short && (
                            <div style={{ fontSize: 10.5, color: '#b45309' }}>asked {amt}</div>
                          )}
                        </span>
                      )
                    }
                    // Asked for, not yet reported collected. This is the actionable state, and it
                    // covers BOTH "still out for delivery" and "delivered but no COD signal" —
                    // CodStatusCodes fails closed on any value not proven to mean collected, so an
                    // unknown code lands here rather than silently reading as paid.
                    return (
                      <span title={s.cod_status ? `Carrier CodStatus: ${s.cod_status}` : 'No collection reported by the carrier yet'}>
                        <b style={{ color: '#b45309' }}>{amt}</b>
                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#9a3412', background: '#ffedd5', border: '1px solid #fdba74', borderRadius: 10, padding: '1px 7px' }}>
                          NOT COLLECTED
                        </span>
                      </span>
                    )
                  })()}</td>
                  )}
                  {isVis('status') && (
                  <td style={td}>
                    <StatusPill status={s.status} />
                    {s.is_combined && (
                      <span title="An Rx was added to this order after its label was purchased"
                        style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#7c3aed', background: '#f3e8ff', border: '1px solid #c4b5fd', borderRadius: 10, padding: '1px 7px', verticalAlign: 'middle' }}>
                        COMBINED
                      </span>
                    )}
                  </td>
                  )}
                  {isVis('shipped') && (
                  <td style={td}>
                    <div>{s.shipped_at ? new Date(s.shipped_at).toLocaleDateString() : '—'}</div>
                  </td>
                  )}
                  {isVis('created_by') && (
                  <td style={td}>
                    {/* Chain of custody: who created this delivery on our side. Imports
                        attribute the COURIER-side entered_by, never the OwlRx user who
                        merely imported the order (misleading — Patrick 2026-07-15). */}
                    {isImport
                      ? (s.courier_entered_by
                          ? <span title="Entered at the courier">{s.courier_entered_by}<span style={{ fontSize: 10, color: 'var(--color-text-muted)' }}> (courier)</span></span>
                          : <span style={{ color: 'var(--color-text-muted)' }}>courier import</span>)
                      : (s.created_by || <span style={{ color: 'var(--color-text-muted)' }}>—</span>)}
                  </td>
                  )}
                  <td style={td} onClick={e => e.stopPropagation()}>
                    {canCreate && (
                      <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        {/* autoclose=1: this tab exists only to print. It auto-prints on
                            load, so leaving it open just accumulates tabs on the dispatch
                            machine — under --kiosk-printing it closes the instant the job
                            is spooled. The tab only closes AFTER print() actually fired. */}
                        {/* POD-6 mirror: no label to print once an order is cancelled / cancel-pending
                            (the drawer already hides its Print — the list button was never gated). */}
                        {s.status !== 'cancelled' && s.status !== 'cancel_pending' && (
                          <button type="button" title="Print label" style={iconBtn}
                            onClick={() => window.open(`/shipments/${s.id}/label?autoclose=1`, '_blank')}>
                            <Printer size={14} />
                          </button>
                        )}
                        {s.status !== 'delivered' && s.status !== 'cancelled' && (
                          <button type="button" title="Cancel shipment" style={iconBtn}
                            disabled={cancelling === s.id}
                            onClick={() => doCancel(s.id)}>
                            <XCircle size={14} />
                          </button>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              )})}
            </tbody>
          </table>
        </div>
        <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="shipments" />
        </>
      )}
      {createOpen && (
        <CreateShipmentDrawer initialRxId={shipRx ?? undefined} initialMoreRxIds={shipRxMore ? shipRxMore.split(',').filter(Boolean) : undefined} fullScreen={fullScreen}
          onClose={() => { setCreateOpen(false); clearShipRx() }}
          onCreated={() => { setCreateOpen(false); clearShipRx(); load() }} />
      )}
      {detailShipmentId && (
        <ShipmentDetailDrawer
          shipmentId={detailShipmentId}
          onClose={() => setDetailShipmentId(null)}
          onCancelled={() => { setDetailShipmentId(null); load() }}
        />
      )}
    </div>
  )
}

function StatusPill({ status }: { status: string }) {
  const tone = STATUS_TONE[status] ?? '#6b7280'
  return (
    <span style={{ ...pill, background: `${tone}1a`, color: tone, borderColor: `${tone}55` }}>
      {status === 'exception' && <AlertTriangle size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} />}
      {statusLabel(status)}
    </span>
  )
}

const STATUS_TONE: Record<string, string> = {
  created: '#6b7280', label_purchased: '#0369a1', in_transit: '#0369a1',
  out_for_delivery: '#a16207', delivered: '#15803d', exception: '#dc2626', cancelled: '#6b7280',
}
function statusLabel(s: string) {
  return ({ created: 'Created', label_purchased: 'Label purchased', in_transit: 'In transit',
    out_for_delivery: 'Out for delivery', delivered: 'Delivered', exception: 'Exception', cancelled: 'Cancelled' } as Record<string, string>)[s] ?? s
}

// Rich detail card for a carrier-lookup result. Designed for support: "Where is
// my package?" should be answerable at a glance — recipient, addresses, status,
// timing, and event timeline if present.
function CarrierLookupCard({ r, onSaved, onError }: {
  r: api.CarrierLookupResult
  onSaved?: (res: api.ImportCarrierShipmentResult) => void
  onError?: (msg: string) => void
}) {
  const [saving, setSaving] = useState(false)
  const [savedMsg, setSavedMsg] = useState<string | null>(null)

  // Compliance: when belongs_to_account is explicitly false, the backend
  // withholds addresses. Render the "exists but not yours" notice instead.
  if (r.belongs_to_account === false) {
    return (
      <div style={lookupBox}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Order {r.order_number}</div>
        <div style={{ fontSize: 13, color: '#a16207' }}>
          {r.detail || 'This order exists on the courier but belongs to a different account. Address details are withheld.'}
        </div>
      </div>
    )
  }
  const dest = r.destination
  const pickup = r.pickup
  return (
    <div style={lookupBox}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>Order {r.order_number}</div>
        <StatusPill status={r.status || 'created'} />
        <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>· {carrierLabel(r.carrier)}</span>
        {r.service && <span style={{ fontSize: 11, padding: '1px 6px', border: '1px solid var(--color-border)', borderRadius: 4, color: 'var(--color-text-muted)' }}>{r.service}</span>}
      </div>
      {r.reference && <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>Reference: {r.reference}</div>}

      {/* Recipient + Pickup */}
      {(dest || pickup) && (
        <div style={twoCol}>
          {dest && (
            <div>
              <div style={lookupLbl}>Deliver to</div>
              <div style={{ fontWeight: 600 }}>{dest.name || r.recipient_name || '—'}</div>
              {dest.line1 && <div style={{ fontSize: 13 }}>{dest.line1}</div>}
              {(dest.city || dest.state || dest.zip) && (
                <div style={{ fontSize: 13 }}>
                  {[dest.city, dest.state].filter(Boolean).join(', ')}{dest.zip ? ` ${dest.zip}` : ''}
                </div>
              )}
              {dest.phone && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{formatPhone(dest.phone)}</div>}
            </div>
          )}
          {pickup && (
            <div>
              <div style={lookupLbl}>Pick up from</div>
              <div style={{ fontWeight: 600 }}>{pickup.name || '—'}</div>
              {pickup.line1 && <div style={{ fontSize: 13 }}>{pickup.line1}</div>}
              {(pickup.city || pickup.state || pickup.zip) && (
                <div style={{ fontSize: 13 }}>
                  {[pickup.city, pickup.state].filter(Boolean).join(', ')}{pickup.zip ? ` ${pickup.zip}` : ''}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Timing */}
      <div style={twoCol}>
        {r.created_at && <TimingRow label="Created" iso={r.created_at} />}
        {r.dispatched_at && <TimingRow label="Dispatched" iso={r.dispatched_at} />}
        {r.picked_up_at && <TimingRow label="Picked up" iso={r.picked_up_at} />}
        {r.pickup_scheduled_at && <TimingRow label="Pickup scheduled" iso={r.pickup_scheduled_at} />}
        {r.delivery_scheduled_at && <TimingRow label="Delivery due" iso={r.delivery_scheduled_at} />}
        {r.delivered_at && <TimingRow label="Delivered" iso={r.delivered_at} />}
        {r.pod_signature_name && (
          <div>
            <div style={lookupLbl}>Signed by</div>
            <div style={{ fontSize: 13 }}>{r.pod_signature_name}</div>
          </div>
        )}
      </div>

      {/* Contents + money */}
      {(r.pieces != null || r.weight != null || r.amount_charged != null || r.declared_value != null || r.service_code) && (
        <div style={twoCol}>
          {r.pieces != null && <CompactKV label="Pieces" value={String(r.pieces)} />}
          {r.weight != null && <CompactKV label="Weight" value={`${r.weight} lb`} />}
          {r.amount_charged != null && <CompactKV label="Amount" value={`$${r.amount_charged.toFixed(2)}`} />}
          {r.declared_value != null && <CompactKV label="Declared value" value={`$${r.declared_value.toFixed(2)}`} />}
          {r.service_code && <CompactKV label="Service" value={r.service_code} />}
        </div>
      )}

      {/* Driver instructions */}
      {r.notes && (
        <div style={{ marginTop: 12, padding: 8, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 6 }}>
          <div style={lookupLbl}>Driver instructions</div>
          <div style={{ fontSize: 13 }}>{r.notes}</div>
        </div>
      )}

      {/* Event timeline — vertical visual */}
      {r.events && r.events.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={lookupLbl}>Activity</div>
          <div style={{ marginTop: 6 }}>
            <VerticalTimeline events={r.events} currentStatus={r.status} />
          </div>
        </div>
      )}

      <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {savedMsg ? (
          <div style={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>{savedMsg}</div>
        ) : (
          <>
            <button type="button" style={primaryBtnSmall} disabled={saving}
              onClick={async () => {
                if (!r.order_number) return
                setSaving(true)
                try {
                  const res = await api.importCarrierShipment(r.order_number, r.carrier_account_id)
                  setSavedMsg(res.created
                    ? `Saved to OwlRx${res.patient_linked ? ' · linked to patient' : ''}`
                    : `Already in OwlRx — tracking refreshed${res.patient_linked ? ' · patient linked' : ''}`)
                  onSaved?.(res)
                } catch (e: any) {
                  onError?.(e?.payload?.message || e?.message || 'Save failed.')
                } finally {
                  setSaving(false)
                }
              }}>
              {saving ? 'Saving…' : 'Save to OwlRx'}
            </button>
            <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
              Pulls this order into the worklist so it gets tracked + auto-updates from now on.
            </span>
          </>
        )}
      </div>
    </div>
  )
}

function TimingRow({ label, iso }: { label: string; iso: string }) {
  return (
    <div>
      <div style={lookupLbl}>{label}</div>
      <div style={{ fontSize: 13 }}>{new Date(iso).toLocaleString()}</div>
    </div>
  )
}
function CompactKV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={lookupLbl}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{value}</div>
    </div>
  )
}

function SummaryStat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div style={summaryStat}>
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: accent || 'var(--color-text)' }}>{value}</div>
    </div>
  )
}

function formatMinutes(m: number) {
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60); const mm = m % 60
  if (h < 24) return mm > 0 ? `${h}h ${mm}m` : `${h}h`
  const d = Math.floor(h / 24); const hh = h % 24
  return hh > 0 ? `${d}d ${hh}h` : `${d}d`
}

// Vertical visual timeline for the carrier-lookup card. Each event gets an icon
// matching its lifecycle stage; current/last event is highlighted. Replaces the
// flat <ol> list to make the order's progress visually scannable.
function VerticalTimeline({ events, currentStatus }:
  { events: { status: string; carrier_status_text?: string | null; description: string | null; location: string | null; occurred_at: string }[]
    currentStatus?: string }) {
  if (!events || events.length === 0) return null
  return (
    <ol style={timelineOl}>
      {events.map((e, i) => {
        const isLast = i === events.length - 1
        const isCurrent = e.status === currentStatus
        return (
          <li key={i} style={timelineLi}>
            <div style={timelineRail}>
              <div style={{ ...timelineDot, background: stageColor(e.status), borderColor: stageColor(e.status) }}>
                {iconForStage(e.status)}
              </div>
              {!isLast && <div style={timelineLine} />}
            </div>
            <div style={{ paddingBottom: isLast ? 0 : 14, flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: isCurrent ? 700 : 500 }}>
                {e.description || stageLabel(e.status)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>
                {e.location ? `${e.location} · ` : ''}{new Date(e.occurred_at).toLocaleString()}
              </div>
            </div>
          </li>
        )
      })}
    </ol>
  )
}
function stageColor(status: string) {
  return ({ created: '#6b7280', label_purchased: '#0369a1', in_transit: '#0369a1',
    out_for_delivery: '#a16207', delivered: '#15803d', exception: '#dc2626', cancelled: '#6b7280' } as Record<string,string>)[status] ?? '#6b7280'
}
function iconForStage(status: string) {
  const sz = 10; const col = '#fff'
  if (status === 'delivered') return <CheckCircle2 size={sz} color={col} />
  if (status === 'out_for_delivery') return <Truck size={sz} color={col} />
  if (status === 'in_transit' || status === 'label_purchased') return <Send size={sz} color={col} />
  return <Circle size={sz} color={col} />
}
function stageLabel(s: string) {
  return ({ created: 'Created', label_purchased: 'Label purchased', in_transit: 'In transit',
    out_for_delivery: 'Out for delivery', delivered: 'Delivered', exception: 'Exception', cancelled: 'Cancelled' } as Record<string, string>)[s] ?? s
}

const refreshBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--color-text-muted)', marginLeft: 'auto' }

const colsPopover: React.CSSProperties = { position: 'absolute', top: '110%', right: 0, zIndex: 30, background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,.12)', padding: 10, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 150 }
const colsItem: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }
const createBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--color-primary, #0369a1)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }
const manualTag: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 6, padding: '0 5px', borderRadius: 6, fontSize: 9, fontWeight: 600, border: '1px solid var(--color-border)', color: 'var(--color-text-muted)', verticalAlign: 'middle' }
const importedTag: React.CSSProperties = { display: 'inline-block', marginLeft: 6, padding: '0 5px', borderRadius: 6, fontSize: 9, fontWeight: 600, border: '1px solid #0369a155', background: '#0369a11a', color: '#0369a1', verticalAlign: 'middle' }
const iconBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)', cursor: 'pointer', color: 'var(--color-text-muted)' }
const filterRow: React.CSSProperties = { display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginTop: 12 }
const filterLabel: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: 'var(--color-text-muted)' }
const dateInput: React.CSSProperties = { padding: '5px 8px', fontSize: 12, border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)' }
const selectInput: React.CSSProperties = { padding: '5px 8px', fontSize: 12, border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)' }
const clearFiltersBtn: React.CSSProperties = { padding: '5px 10px', fontSize: 11, border: '1px solid var(--color-border)', borderRadius: 6, background: 'transparent', cursor: 'pointer', color: 'var(--color-text-muted)' }
const csvBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 10px', fontSize: 12, border: '1px solid var(--color-border)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', textDecoration: 'none' }
const summaryPanel: React.CSSProperties = { display: 'flex', gap: 24, flexWrap: 'wrap', padding: '14px 16px', border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-surface)', marginTop: 12 }
const summaryStat: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 80 }
const timelineOl: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0 }
const timelineLi: React.CSSProperties = { display: 'flex', gap: 10 }
const timelineRail: React.CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center' }
const timelineDot: React.CSSProperties = { width: 18, height: 18, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid', flexShrink: 0 }
const timelineLine: React.CSSProperties = { width: 2, flex: 1, background: 'var(--color-border)', marginTop: 2, minHeight: 14 }
const searchWrap: React.CSSProperties = { position: 'relative', marginTop: 12 }
const searchInput: React.CSSProperties = { width: '100%', padding: '8px 32px 8px 32px', fontSize: 13, border: '1px solid var(--color-border)', borderRadius: 8, boxSizing: 'border-box', background: 'var(--color-surface)' }
const searchClear: React.CSSProperties = { position: 'absolute', right: 8, top: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: 4 }
const primaryBtnSmall: React.CSSProperties = { background: 'var(--color-primary, #0369a1)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 12px', fontSize: 13, cursor: 'pointer' }
const lookupBox: React.CSSProperties = { background: 'var(--color-bg, #f8fafc)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 16, fontSize: 13 }
const twoCol: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginTop: 12 }
const lookupLbl: React.CSSProperties = { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--color-text-muted)', marginBottom: 2 }
const chip: React.CSSProperties = { padding: '5px 12px', borderRadius: 16, fontSize: 13, border: '1px solid var(--color-border)', background: 'var(--color-surface)', cursor: 'pointer', color: 'var(--color-text)' }
const chipActive: React.CSSProperties = { background: 'var(--color-primary, #0369a1)', color: '#fff', borderColor: 'var(--color-primary, #0369a1)' }
const tableWrap: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const th: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--color-border)', fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 600, background: 'var(--color-bg, #f8fafc)' }
const td: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid var(--color-border)' }
const row: React.CSSProperties = { cursor: 'pointer' }
const pill: React.CSSProperties = { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, border: '1px solid' }
const mutedText: React.CSSProperties = { color: 'var(--color-text-muted)', fontSize: 13 }
const emptyBox: React.CSSProperties = { textAlign: 'center', padding: '48px 20px', border: '1px dashed var(--color-border)', borderRadius: 8 }
const errorBox: React.CSSProperties = { padding: 10, background: '#dc26260d', border: '1px solid #dc262633', borderRadius: 6, fontSize: 13, color: '#dc2626', marginBottom: 12 }
